import React, { useState } from 'react';
import InvoiceList from './InvoiceList';
import ARChasing from './ARChasing';
import InvoiceForm from './InvoiceForm';
import PaymentForm from '../PaymentManagement/PaymentForm';
import invoiceService, { Invoice } from '../../../../services/invoiceService';
import { Modal, Descriptions, Tag, Table, Typography, message, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const InvoiceManagementNew: React.FC = () => {
    const { t } = useTranslation();
  const [formVisible, setFormVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [paymentFormVisible, setPaymentFormVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceForPayment, setInvoiceForPayment] = useState<Invoice | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreate = () => {
    setSelectedInvoice(null);
    setFormVisible(true);
  };

  const handleEdit = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details with items
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      setSelectedInvoice(fullInvoice);
      setFormVisible(true);
    } catch (error: any) {
      message.error(t('Failed to load invoice: {{message}}', { message: error.message }));
    }
  };

  const handleDuplicate = async (invoice: Invoice) => {
    try {
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      // Clone invoice without id/invoiceNo so the form creates a new one
      const duplicated = {
        ...fullInvoice,
        id: undefined,
        invoiceNo: undefined,
        status: 'DRAFT',
        items: fullInvoice.items?.map((item: any) => ({
          ...item,
          id: undefined,
          invoiceId: undefined,
        })),
        optionalItems: fullInvoice.optionalItems?.map((item: any) => ({
          ...item,
          id: undefined,
          invoiceId: undefined,
        })),
      };
      setSelectedInvoice(duplicated as any);
      setFormVisible(true);
      message.info(t('Invoice duplicated - review and save as new'));
    } catch (error: any) {
      message.error(t('Failed to duplicate invoice: {{message}}', { message: error.message }));
    }
  };

  const handleView = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details with items
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      setSelectedInvoice(fullInvoice);
      setViewModalVisible(true);
    } catch (error: any) {
      message.error(t('Failed to load invoice: {{message}}', { message: error.message }));
    }
  };

  const handleFormClose = () => {
    setFormVisible(false);
    setSelectedInvoice(null);
  };

  const handleFormSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleMarkPaid = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      setInvoiceForPayment(fullInvoice);
      setPaymentFormVisible(true);
    } catch (error: any) {
      message.error(t('Failed to load invoice: {{message}}', { message: error.message }));
    }
  };

  const handlePaymentFormClose = () => {
    setPaymentFormVisible(false);
    setInvoiceForPayment(null);
  };

  const handlePaymentFormSuccess = () => {
    setRefreshKey(prev => prev + 1);
    message.success(t('Payment created and invoice marked as paid successfully'));
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      SENT: 'processing',
      PAID: 'success',
      OVERDUE: 'error',
      CANCELLED: 'default'
    };
    return colors[status] || 'default';
  };

  const itemColumns: ColumnsType<any> = [
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: t('Item Code'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      render: (code) => code || '-'
    },
    {
      title: t('Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center'
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      render: (price) => `$${Number(price).toFixed(2)}`
    },
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      align: 'right',
      render: (total) => (
        <strong>${Number(total).toFixed(2)}</strong>
      )
    }
  ];

  return (
    <div>
      <Tabs
        defaultActiveKey="invoices"
        items={[
          {
            key: 'invoices',
            label: t('Invoices'),
            children: (
              <InvoiceList
                key={refreshKey}
                onCreate={handleCreate}
                onEdit={handleEdit}
                onView={handleView}
                onMarkPaid={handleMarkPaid}
                onDuplicate={handleDuplicate}
              />
            )
          },
          {
            key: 'chasing',
            label: t('AR Chasing'),
            // Mounted only when opened — it pulls every overdue invoice.
            children: <ARChasing />
          }
        ]}
      />

      <InvoiceForm
        visible={formVisible}
        invoice={selectedInvoice}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />

      <PaymentForm
        visible={paymentFormVisible}
        invoice={invoiceForPayment}
        onClose={handlePaymentFormClose}
        onSuccess={handlePaymentFormSuccess}
      />

      <Modal
        title={t('Invoice Details')}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedInvoice && (
          <div>
            <Descriptions column={2} bordered>
              <Descriptions.Item label={t('Invoice No')}>
                <strong>{selectedInvoice.invoiceNo}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('Status')}>
                <Tag color={getStatusColor(selectedInvoice.status)}>
                  {selectedInvoice.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Customer')}>
                {selectedInvoice.customer?.name || selectedInvoice.customerId}
              </Descriptions.Item>
              <Descriptions.Item label={t('Created By')}>
                {selectedInvoice.user?.name || selectedInvoice.userId}
              </Descriptions.Item>
              <Descriptions.Item label={t('Created Date')}>
                {dayjs(selectedInvoice.createdAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('Due Date')}>
                {selectedInvoice.dueDate ? dayjs(selectedInvoice.dueDate).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Total Amount')} span={2}>
                <Text strong style={{ fontSize: 18 }}>
                  ${Number(selectedInvoice.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
              {t('Line Items')}
                                      </Title>
            <Table
              columns={itemColumns}
              dataSource={selectedInvoice.items}
              pagination={false}
              rowKey="id"
              size="small"
            />

            {(selectedInvoice.paymentTerms || selectedInvoice.termsConditions) && (
              <Descriptions column={1} bordered style={{ marginTop: 24 }}>
                {selectedInvoice.paymentTerms && (
                  <Descriptions.Item label={t('Payment Terms')}>
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                      {selectedInvoice.paymentTerms}
                    </div>
                  </Descriptions.Item>
                )}
                {selectedInvoice.termsConditions && (
                  <Descriptions.Item label={t('Terms & Conditions')}>
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                      {selectedInvoice.termsConditions}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceManagementNew;